/* eslint-disable global-require */
/* eslint-disable @typescript-eslint/no-require-imports */

import { RemoteEntryModule } from '../types';
import { SharedModuleNames } from './shared-modules-meta';

type SharedScopeObject = {
  [moduleName: string]: {
    [versionRange: string]: {
      get: () => Promise<() => any>;
      eager?: boolean;
      loaded?: 1;
    };
  };
};

const initSharedScope = () => {
  const scope: SharedScopeObject = {};

  // If version range is '*' it means "this shared module matches all requested versions",
  // i.e. make sure that the plugin always uses the given shared module implementation
  const addModule = (
    moduleName: SharedModuleNames,
    getModule: () => Promise<() => any>,
    versionRange = '*',
  ) => {
    scope[moduleName] = scope[moduleName] ?? {};
    scope[moduleName][versionRange] = { get: getModule, eager: true, loaded: 1 };
  };

  // Singleton modules provided by Console application without plugin provided fallback
  addModule('@openshift-console/dynamic-plugin-sdk', async () => () =>
    require('@console/dynamic-plugin-sdk/src/lib-core'),
  );
  addModule('@openshift-console/dynamic-plugin-sdk-internal', async () => () =>
    require('@console/dynamic-plugin-sdk/src/lib-internal'),
  );
  addModule('@patternfly/react-topology', async () => () => require('@patternfly/react-topology'));
  addModule('react', async () => () => require('react'));
  addModule('react-i18next', async () => () => require('react-i18next'));
  addModule('react-redux', async () => () => require('react-redux'));
  addModule('react-router', async () => () => require('react-router'));
  addModule('react-router-dom', async () => () => require('react-router-dom'));
  addModule('react-router-dom-v5-compat', async () => () => require('react-router-dom-v5-compat'));
  addModule('redux', async () => () => require('redux'));
  addModule('redux-thunk', async () => () => require('redux-thunk'));

  return scope;
};

const sharedScope = initSharedScope();

if (process.env.NODE_ENV !== 'production') {
  // Expose webpack shared scope object for debugging
  window.webpackSharedScope = sharedScope;
}

/**
 * At runtime, the Console application will initialize shared modules for each
 * dynamic plugin before loading any of the modules exposed by the given plugin.
 *
 * Note: `__webpack_init_sharing__` global function is available in webpack 5+ builds.
 * Once Console gets built with webpack 5, evaluate if we need this global in order to
 * allow plugins to attempt to provide shared modules into the application shared scope.
 *
 * @see https://webpack.js.org/concepts/module-federation/#dynamic-remote-containers
 */
export const initSharedPluginModules = (entryModule: RemoteEntryModule) => {
  entryModule.init(sharedScope);
};
